import React, { useState, useEffect } from "react"
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material"
import {
  LocalShipping as ShippingIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material"
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet"
import L from "leaflet"
import "leaflet/dist/leaflet.css"
import { apiService } from "../services/apiService"

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
})

interface TrackingData {
  activeShipments: Array<{
    shipmentId: string
    shipmentNumber: string
    status: string
    currentLocation: {
      latitude: number
      longitude: number
      address: string
      facilityCode?: string
    }
    lastUpdated: string
    estimatedDelivery: string
    carrier: string
    carrierReference: string
  }>
  summary: {
    totalActiveShipments: number
    inTransit: number
    outForDelivery: number
    atFacility: number
    deliveredToday: number
    exceptionsToday: number
    onTimeDeliveryRate: number
    averageTransitTime: number
  }
  recentEvents: Array<{
    id: string
    shipmentId: string
    eventType: string
    status: string
    location: string
    timestamp: string
    source: string
  }>
}

const Tracking: React.FC = () => {
  const [trackingData, setTrackingData] = useState<TrackingData | null>(null)
  const [loading, setLoading] = useState(true)
  const [trackingDialogOpen, setTrackingDialogOpen] = useState(false)
  const [shipmentTracking, setShipmentTracking] = useState<any>(null)

  useEffect(() => {
    fetchTrackingData()
    const interval = setInterval(fetchTrackingData, 30000) // Refresh every 30 seconds
    return () => clearInterval(interval)
  }, [])

  const fetchTrackingData = async () => {
    try {
      setLoading(true)
      const data = await apiService.getTrackingData()
      setTrackingData(data)
    } catch (error) {
      console.error("Failed to fetch tracking data:", error)
    } finally {
      setLoading(false)
    }
  }

  const fetchShipmentTracking = async (shipmentId: string) => {
    try {
      const data = await apiService.getShipmentTracking(shipmentId)
      setShipmentTracking(data)
      setTrackingDialogOpen(true)
    } catch (error) {
      console.error("Failed to fetch shipment tracking:", error)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "IN_TRANSIT":
        return "primary"
      case "OUT_FOR_DELIVERY":
        return "info"
      case "AT_FACILITY":
        return "warning"
      case "DELIVERED":
        return "success"
      case "EXCEPTION":
        return "error"
      default:
        return "default"
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "IN_TRANSIT":
        return <ShippingIcon />
      case "OUT_FOR_DELIVERY":
        return <LocationIcon />
      case "AT_FACILITY":
        return <ScheduleIcon />
      case "DELIVERED":
        return <CheckCircleIcon />
      case "EXCEPTION":
        return <WarningIcon />
      default:
        return <ShippingIcon />
    }
  }

  if (loading) {
    return <Box>Loading tracking data...</Box>
  }

  if (!trackingData) {
    return <Alert severity="error">Failed to load tracking data</Alert>
  }

  // Calculate center of all active shipments for map
  const centerLat =
    trackingData.activeShipments.reduce(
      (sum, s) => sum + s.currentLocation.latitude,
      0
    ) / trackingData.activeShipments.length
  const centerLng =
    trackingData.activeShipments.reduce(
      (sum, s) => sum + s.currentLocation.longitude,
      0
    ) / trackingData.activeShipments.length

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Box>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              background: "linear-gradient(135deg, #1a237e 0%, #534bae 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              mb: 1,
            }}
          >
            Real-Time Tracking
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ fontWeight: 400 }}
          >
            Live shipment tracking and monitoring
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchTrackingData}
        >
          Refresh
        </Button>
      </Box>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Active Shipments
                  </Typography>
                  <Typography variant="h4">
                    {trackingData.summary.totalActiveShipments}
                  </Typography>
                </Box>
                <ShippingIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    In Transit
                  </Typography>
                  <Typography variant="h4">
                    {trackingData.summary.inTransit}
                  </Typography>
                </Box>
                <LocationIcon color="info" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Delivered Today
                  </Typography>
                  <Typography variant="h4">
                    {trackingData.summary.deliveredToday}
                  </Typography>
                </Box>
                <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Exceptions
                  </Typography>
                  <Typography
                    variant="h4"
                    color={
                      trackingData.summary.exceptionsToday > 0
                        ? "error"
                        : "success"
                    }
                  >
                    {trackingData.summary.exceptionsToday}
                  </Typography>
                </Box>
                <WarningIcon
                  color={
                    trackingData.summary.exceptionsToday > 0
                      ? "error"
                      : "success"
                  }
                  sx={{ fontSize: 40 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Map and Shipments List */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Live Shipment Tracking Map
              </Typography>
              <Box height={400}>
                <MapContainer
                  center={[centerLat, centerLng]}
                  zoom={4}
                  style={{ height: "100%", width: "100%" }}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  />
                  {trackingData.activeShipments.map((shipment) => (
                    <Marker
                      key={shipment.shipmentId}
                      position={[
                        shipment.currentLocation.latitude,
                        shipment.currentLocation.longitude,
                      ]}
                    >
                      <Popup>
                        <Box>
                          <Typography variant="subtitle2" gutterBottom>
                            {shipment.shipmentNumber}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Status: {shipment.status.replace("_", " ")}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Carrier: {shipment.carrier}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Location: {shipment.currentLocation.address}
                          </Typography>
                          <Button
                            size="small"
                            onClick={() =>
                              fetchShipmentTracking(shipment.shipmentId)
                            }
                            sx={{ mt: 1 }}
                          >
                            View Details
                          </Button>
                        </Box>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Active Shipments
              </Typography>
              <List>
                {trackingData.activeShipments.map((shipment, index) => (
                  <React.Fragment key={shipment.shipmentId}>
                    <ListItemButton
                      onClick={() => fetchShipmentTracking(shipment.shipmentId)}
                    >
                      <ListItemIcon>
                        {getStatusIcon(shipment.status)}
                      </ListItemIcon>
                      <ListItemText
                        primary={shipment.shipmentNumber}
                        secondary={
                          <Box>
                            <Typography variant="body2" color="textSecondary">
                              {shipment.currentLocation.address}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                              <Chip
                                label={shipment.status.replace("_", " ")}
                                color={getStatusColor(shipment.status) as any}
                                size="small"
                              />
                              <Chip
                                label={shipment.carrier}
                                variant="outlined"
                                size="small"
                              />
                            </Box>
                            <Typography variant="caption" color="textSecondary">
                              Last updated:{" "}
                              {new Date(shipment.lastUpdated).toLocaleString()}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItemButton>
                    {index < trackingData.activeShipments.length - 1 && (
                      <Divider />
                    )}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Events */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recent Tracking Events
          </Typography>
          <List>
            {trackingData.recentEvents.map((event, index) => (
              <React.Fragment key={event.id}>
                <ListItem>
                  <ListItemIcon>{getStatusIcon(event.status)}</ListItemIcon>
                  <ListItemText
                    primary={`${event.eventType.replace("_", " ")} - ${event.shipmentId}`}
                    secondary={
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          {event.location} • {event.source}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {new Date(event.timestamp).toLocaleString()}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
                {index < trackingData.recentEvents.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* Shipment Tracking Dialog */}
      <Dialog
        open={trackingDialogOpen}
        onClose={() => setTrackingDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Shipment Tracking Details</DialogTitle>
        <DialogContent>
          {shipmentTracking && (
            <Box>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Shipment Number
                  </Typography>
                  <Typography variant="body1">
                    {shipmentTracking.shipmentId}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Current Status
                  </Typography>
                  <Chip
                    label={shipmentTracking.currentStatus.replace("_", " ")}
                    color={
                      getStatusColor(shipmentTracking.currentStatus) as any
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Current Location
                  </Typography>
                  <Typography variant="body1">
                    {shipmentTracking.currentLocation.address}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="h6" gutterBottom>
                    Tracking History
                  </Typography>
                  <List>
                    {shipmentTracking.trackingHistory.map(
                      (event: any, index: number) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            {getStatusIcon(event.status)}
                          </ListItemIcon>
                          <ListItemText
                            primary={`${event.eventType.replace("_", " ")} - ${event.status.replace("_", " ")}`}
                            secondary={
                              <Box>
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                >
                                  {event.location.address}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                >
                                  {new Date(event.timestamp).toLocaleString()}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      )
                    )}
                  </List>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTrackingDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default Tracking
